import { Box, Button, Checkbox, FormControlLabel, FormGroup, Link, Tooltip } from '@mui/material'
import { t } from 'i18next'
import { useState } from 'react'

import { useAlertContext } from '@/contexts/AlertContext'
import environment from '@/environment'
import ResourceSupportToolboxFeatureToggleTable from '@/features/resource/components/ResourceSupportToolboxFeatureToggleTable'
import { isMonolithSupportActionVisible } from '@/features/resource/components/utils/resourceSupportToolboxVisibility'
import { useDecommissionResourceMutation } from '@/features/resource/hooks/useDecommissionResourceMutation'
import { useRepublishResourceMutation } from '@/features/resource/hooks/useRepublishResourceMutation'
import { useInvalidateResourceQuery } from '@/features/resource/hooks/useResourceQuery'
import { useResyncResourceMutation } from '@/features/resource/hooks/useResyncResourceMutation'
import { useSyncResourceComponentStatusesMutation } from '@/features/resource/hooks/useSyncResourceComponentStatusesMutation'
import { generateLogSearchUrl } from '@/utils/coralogixLinkGenerator'
import { errorHandler } from '@/utils/errorHandler'

const ResourceSupportToolbox = ({ resource }) => {
  const resourceId = resource.resourceID
  const showMonolithActions = isMonolithSupportActionVisible(resource)

  const { pushAlert } = useAlertContext()

  const loggingUrl = generateLogSearchUrl([`message.contains('${resourceId}')`], 'last-15-minutes')
  const grafanaUrl = environment.dashboards.grafanaSupportToolboxUrl.replace('~resource-placeholder~', resourceId)

  const { decommissionResource } = useDecommissionResourceMutation()
  const { republishResource } = useRepublishResourceMutation()
  const { resyncResource } = useResyncResourceMutation()
  const { syncResourceComponentStatuses } = useSyncResourceComponentStatusesMutation()
  const invalidateResource = useInvalidateResourceQuery()

  // Config + steering republish are monolith-fed projections that LOCAL resources don't use, so
  // their checkboxes are hidden on LOCAL — initialise them to false there so a Republish click
  // never sends a payload that would attempt to re-emit a projection that doesn't apply.
  const [flags, setFlags] = useState(() => ({
    enableConfigRepublish: showMonolithActions,
    enableSteeringRepublish: showMonolithActions,
    enableSteerabilityAndBatteryRepublish: true,
  }))

  const noRepublishFlagChecked =
    !flags.enableConfigRepublish && !flags.enableSteeringRepublish && !flags.enableSteerabilityAndBatteryRepublish

  // Each action below lands an authoritative state change (lifecycleStage, port error /
  // steerability, steering status, component statuses). Without the explicit invalidate, the
  // surrounding ResourceCard header stays stale until the ~5 s polling refetch in
  // useResourceWithPollingQuery kicks in. Invalidate the detail query after every successful
  // mutateAsync so the UI catches up immediately.
  const handleDecommission = async () => {
    try {
      await decommissionResource(resourceId)
      await invalidateResource(resourceId)
    } catch (err) {
      const error = errorHandler(err, t('resources.support_toolbox.errors.decommission_error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  const handleResync = async () => {
    try {
      await resyncResource(resourceId)
      await invalidateResource(resourceId)
    } catch (err) {
      const error = errorHandler(err, t('resources.support_toolbox.errors.resync_error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  const handleRepublish = async () => {
    try {
      const request = {
        config: flags.enableConfigRepublish,
        steering: flags.enableSteeringRepublish,
        steerabilityAndBattery: flags.enableSteerabilityAndBatteryRepublish,
      }
      await republishResource({ resourceId, request })
      await invalidateResource(resourceId)
    } catch (err) {
      const error = errorHandler(err, t('resources.support_toolbox.errors.republish_error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  const handleSyncResourceComponentStatuses = async () => {
    try {
      await syncResourceComponentStatuses(resourceId)
      await invalidateResource(resourceId)
    } catch (err) {
      const error = errorHandler(err, t('resources.support_toolbox.errors.sync_component_statuses_error'))
      pushAlert({ message: error.message, severity: 'error' })
    }
  }

  return (
    <>
      <Box component="header" sx={{ marginBottom: 2 }}>
        {t('resources.support_toolbox.title')}
      </Box>
      <Box component="section">
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ marginBottom: 2 }}>
              <Link href={grafanaUrl} rel="noopener noreferrer" target="_blank">
                {t('resources.support_toolbox.grafana_link_label')}
              </Link>
            </Box>
            <Box sx={{ marginBottom: 2 }}>
              <Link href={loggingUrl} rel="noopener noreferrer" target="_blank">
                {t('resources.support_toolbox.logging_link_label')}
              </Link>
            </Box>
            <Box sx={{ marginBottom: 2 }}>
              <Button fullWidth color="secondary" variant="contained" onClick={handleDecommission}>
                {t('resources.support_toolbox.decommission_button_label')}
              </Button>
            </Box>
            {showMonolithActions && (
              <Box sx={{ marginBottom: 2 }}>
                <Tooltip arrow title={t('resources.support_toolbox.resync_config.tooltip')}>
                  <Button fullWidth color="secondary" variant="contained" onClick={handleResync}>
                    {t('resources.support_toolbox.resync_config.button_label')}
                  </Button>
                </Tooltip>
              </Box>
            )}
            {showMonolithActions && (
              <Box sx={{ marginBottom: 2 }}>
                <Tooltip arrow title={t('resources.support_toolbox.sync_ressource_component_statuses.tooltip')}>
                  <Button fullWidth color="secondary" variant="contained" onClick={handleSyncResourceComponentStatuses}>
                    {t('resources.support_toolbox.sync_ressource_component_statuses.button_label')}
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ border: '1px solid #ccc', borderRadius: 2, padding: 2, marginBottom: 2 }}>
              <FormGroup>
                {showMonolithActions && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={flags.enableConfigRepublish}
                        color="primary"
                        onChange={() =>
                          setFlags((prevFlags) => ({
                            ...prevFlags,
                            enableConfigRepublish: !prevFlags.enableConfigRepublish,
                          }))
                        }
                      />
                    }
                    label={t('resources.support_toolbox.republish.config_label')}
                  />
                )}
                {showMonolithActions && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={flags.enableSteeringRepublish}
                        color="primary"
                        onChange={() =>
                          setFlags((prevFlags) => ({
                            ...prevFlags,
                            enableSteeringRepublish: !prevFlags.enableSteeringRepublish,
                          }))
                        }
                      />
                    }
                    label={t('resources.support_toolbox.republish.steering_label')}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={flags.enableSteerabilityAndBatteryRepublish}
                      color="primary"
                      onChange={() =>
                        setFlags((prevFlags) => ({
                          ...prevFlags,
                          enableSteerabilityAndBatteryRepublish: !prevFlags.enableSteerabilityAndBatteryRepublish,
                        }))
                      }
                    />
                  }
                  label={t('resources.support_toolbox.republish.steerability_and_battery_label')}
                />
              </FormGroup>
              <Tooltip arrow title={t('resources.support_toolbox.republish.tooltip')}>
                {/* `<span>` wrapper so the Tooltip still binds to a focusable target even when
                 * the Button is disabled (MUI suppresses pointer events on disabled buttons). */}
                <span>
                  <Button
                    fullWidth
                    color="secondary"
                    disabled={noRepublishFlagChecked}
                    variant="contained"
                    onClick={handleRepublish}
                  >
                    {t('resources.support_toolbox.republish.button_label')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <ResourceSupportToolboxFeatureToggleTable resource={resource} />
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default ResourceSupportToolbox
